import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  LayoutDashboard, Truck, History, DollarSign, ListFilter, Plus, ArrowUp, 
  Users, Car, Bell, Settings, MapPin, Calendar, Clock, Package, Eye,
  Filter, Search, MoreVertical, Edit, Trash2, ToggleLeft, ToggleRight,
  AlertTriangle, CheckCircle, XCircle, Camera, X
} from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import { useTransporterVehicles } from '@/hooks/useTransporterVehicles';
import { useTransporterStats } from '@/hooks/useTransporterStats';
import { useVehicleTypes } from '@/hooks/useVehicleTypes';
import { getTransporterSidebarLinks } from '@/config/transporterSidebarLinks';
import useAuthStore from '@/store/authStore';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';

const TransporterVehicles = () => {
  const { stats } = useTransporterStats();
  const { vehicleTypes, loading: typesLoading } = useVehicleTypes();
  const { user } = useAuthStore();
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
  });
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const [vehicleData, setVehicleData] = useState({
    plate_number: '',
    vehicle_type: '',
    make: '',
    model: '',
    year: '',
    capacity_volume: '',
    capacity_weight: '',
    insurance_valid_until: '',
    technical_control_until: '',
    image: undefined,
  });
  const [imagePreview, setImagePreview] = useState(null);
  
  const { vehicles, loading, error, createVehicle, updateVehicle, deleteVehicle, toggleAvailability } = useTransporterVehicles(filters);
  
  const sidebarLinks = getTransporterSidebarLinks(stats);

  const getStatusBadge = (status) => {
    const statusConfig = {
      'AVAILABLE': { label: 'Disponible', color: 'bg-green-500' },
      'ON_MISSION': { label: 'En mission', color: 'bg-blue-500' },
      'MAINTENANCE': { label: 'Maintenance', color: 'bg-orange-500' },
      'INACTIVE': { label: 'Inactif', color: 'bg-gray-500' },
    };
    
    const config = statusConfig[status] || { label: status, color: 'bg-gray-500' };
    return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
  };

  const getStatusIcon = (status) => {
    const iconConfig = {
      'AVAILABLE': CheckCircle,
      'ON_MISSION': Truck,
      'MAINTENANCE': AlertTriangle,
      'INACTIVE': XCircle,
    };
    
    const Icon = iconConfig[status] || XCircle;
    return <Icon className="w-4 h-4" />;
  };

  const handleCreateVehicle = () => {
    setEditingVehicle(null);
    setVehicleData({
      plate_number: '',
      vehicle_type: '',
      make: '',
      model: '',
      year: '',
      capacity_volume: '',
      capacity_weight: '',
      insurance_valid_until: '',
      technical_control_until: '',
      image: undefined,
    });
    setImagePreview(null);
    setVehicleDialogOpen(true);
  };

  const handleEditVehicle = (vehicle) => {
    setEditingVehicle(vehicle);
    setVehicleData({
      plate_number: vehicle.plate_number,
      vehicle_type: vehicle.vehicle_type?.code || '',
      make: vehicle.make || '',
      model: vehicle.model || '',
      year: vehicle.year || '',
      capacity_volume: vehicle.capacity_volume || '',
      capacity_weight: vehicle.capacity_weight || '',
      insurance_valid_until: vehicle.insurance_valid_until || '',
      technical_control_until: vehicle.technical_control_until || '',
      image: undefined,
    });
    setImagePreview(vehicle.image || null);
    setVehicleDialogOpen(true);
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Vérifier la taille du fichier (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast({
          title: 'Erreur',
          description: 'L\'image ne doit pas dépasser 2MB',
          variant: 'destructive',
        });
        return;
      }

      // Vérifier le type de fichier
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Erreur',
          description: 'Veuillez sélectionner un fichier image (JPG, PNG, GIF)',
          variant: 'destructive',
        });
        return;
      }

      // Redimensionner l'image pour optimiser l'affichage
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Définir les dimensions maximales
        const maxWidth = 800;
        const maxHeight = 600;
        
        let { width, height } = img;
        
        // Calculer les nouvelles dimensions en gardant le ratio
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }
        
        // Redimensionner l'image
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convertir en blob
        canvas.toBlob((blob) => {
          if (blob) {
            // Créer un nouveau fichier avec l'image redimensionnée
            const resizedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            
            setVehicleData(prev => ({ ...prev, image: resizedFile }));
            
            // Créer un aperçu
            const reader = new FileReader();
            reader.onload = (e) => {
              setImagePreview(e.target.result);
            };
            reader.readAsDataURL(resizedFile);
            
            toast({
              title: 'Succès',
              description: 'Image redimensionnée et optimisée',
            });
          }
        }, 'image/jpeg', 0.8);
      };
      
      img.src = URL.createObjectURL(file);
    }
  };

  const handleSubmitVehicle = async () => {
    if (!vehicleData.plate_number || !vehicleData.vehicle_type) {
      toast({
        title: 'Erreur',
        description: 'La plaque d\'immatriculation et le type de véhicule sont requis.',
        variant: 'destructive',
      });
      return;
    }

    // Récupérer l'ID du transporteur depuis le profil utilisateur
    const transporterId = user?.profile_data?.id;
    if (!transporterId && !editingVehicle) {
      toast({
        title: 'Erreur',
        description: 'Impossible de récupérer l\'ID du transporteur. Veuillez vous reconnecter.',
        variant: 'destructive',
      });
      return;
    }

    // Préparer les données à envoyer
    let submitData;
    
    // Si il y a une image, utiliser FormData, sinon JSON
    if (vehicleData.image && vehicleData.image instanceof File) {
      submitData = new FormData();
      
      // Ajouter l'ID du transporteur si c'est une création
      if (!editingVehicle && transporterId) {
        submitData.append('transporter', transporterId);
      }
      
      // Ajouter les champs au FormData (exclure l'image pour l'instant)
      Object.keys(vehicleData).forEach(key => {
        // Ignorer l'image ici, on l'ajoutera après
        if (key === 'image') return;
        
        const value = vehicleData[key];
        
        // Ignorer les valeurs vides, null ou undefined
        if (value === undefined || value === null || value === '') {
          return;
        }
        
        // Gérer les dates (déjà au format YYYY-MM-DD depuis l'input date)
        if (key === 'insurance_valid_until' || key === 'technical_control_until') {
          // Les dates sont déjà au format YYYY-MM-DD, on garde tel quel
          // S'assurer que c'est bien une string valide
          if (value && typeof value === 'string' && value.trim() !== '') {
            submitData.append(key, value.trim());
          }
        } 
        // Gérer les nombres
        else if (key === 'capacity_volume' || key === 'capacity_weight' || key === 'year') {
          submitData.append(key, value.toString());
        } 
        // Autres champs (strings)
        else {
          submitData.append(key, value);
        }
      });
      
      // Ajouter l'image séparément si c'est un File
      if (vehicleData.image && vehicleData.image instanceof File) {
        submitData.append('image', vehicleData.image);
      }
    } else {
      // Utiliser JSON pour les données sans image
      submitData = { ...vehicleData };
      
      // Ajouter l'ID du transporteur si c'est une création
      if (!editingVehicle && transporterId) {
        submitData.transporter = transporterId;
      }
      
      // Nettoyer les valeurs vides et undefined
      const requiredFields = ['plate_number', 'vehicle_type'];
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === undefined || submitData[key] === null || 
            (submitData[key] === '' && !requiredFields.includes(key))) {
          delete submitData[key];
        }
      });
      
      // Convertir les valeurs numériques
      if (submitData.year) {
        submitData.year = parseInt(submitData.year);
      }
      if (submitData.capacity_volume) {
        submitData.capacity_volume = parseFloat(submitData.capacity_volume);
      }
      if (submitData.capacity_weight) {
        submitData.capacity_weight = parseFloat(submitData.capacity_weight);
      }
      
      // Convertir les dates en format ISO string
      if (submitData.insurance_valid_until) {
        submitData.insurance_valid_until = submitData.insurance_valid_until.split('T')[0];
      }
      if (submitData.technical_control_until) {
        submitData.technical_control_until = submitData.technical_control_until.split('T')[0];
      }
      
      // Supprimer l'image si ce n'est pas un fichier
      delete submitData.image;
    }

    // Debug: Afficher les données envoyées
    console.log('🚗 Données du véhicule à envoyer:', submitData);
    if (submitData instanceof FormData) {
      console.log('📋 FormData contents:');
      for (let pair of submitData.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }
    }

    const result = editingVehicle 
      ? await updateVehicle(editingVehicle.id, submitData)
      : await createVehicle(submitData);

    if (result.success) {
      toast({
        title: editingVehicle ? 'Véhicule modifié' : 'Véhicule créé',
        description: `Le véhicule a été ${editingVehicle ? 'modifié' : 'créé'} avec succès.`,
      });
      setVehicleDialogOpen(false);
      setImagePreview(null);
    } else {
      toast({
        title: 'Erreur',
        description: result.error,
        variant: 'destructive',
      });
    }
  };

  const handleDeleteVehicle = (vehicle) => {
    setVehicleToDelete(vehicle);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteVehicle = async () => {
    const result = await deleteVehicle(vehicleToDelete.id);
    if (result.success) {
      toast({
        title: 'Véhicule supprimé',
        description: 'Le véhicule a été supprimé avec succès.',
      });
    } else {
      toast({
        title: 'Erreur',
        description: result.error,
        variant: 'destructive',
      });
    }
    setDeleteDialogOpen(false);
    setVehicleToDelete(null);
  };

  const handleToggleAvailability = async (vehicle) => {
    const result = await toggleAvailability(vehicle.id);
    if (result.success) {
      toast({
        title: 'Statut modifié',
        description: `Le véhicule est maintenant ${result.data.status === 'AVAILABLE' ? 'disponible' : 'indisponible'}.`,
      });
    } else {
      toast({
        title: 'Erreur',
        description: result.error,
        variant: 'destructive',
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifiée';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const isExpired = (dateString) => {
    if (!dateString) return false;
    return new Date(dateString) < new Date();
  };

  const isExpiringSoon = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return date <= thirtyDaysFromNow && date > new Date();
  };

  return (
    <>
      <Helmet>
        <title>Gestion des Véhicules - LogiConnect</title>
      </Helmet>
      <DashboardLayout sidebarLinks={sidebarLinks}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">Gestion des Véhicules</h1>
            <p className="text-muted-foreground mt-2">Gérez votre flotte de véhicules</p>
          </div>
          <Button onClick={handleCreateVehicle} className="bg-primary hover:bg-primary/90">
            <Plus className="w-5 h-5 mr-2" />
            Ajouter un véhicule
          </Button>
        </div>

        {/* Filtres */}
        <Card className="mb-6 bg-card/50 border-border">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Rechercher un véhicule..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={filters.status} onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="Filtrer par statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="AVAILABLE">Disponible</SelectItem>
                  <SelectItem value="ON_MISSION">En mission</SelectItem>
                  <SelectItem value="MAINTENANCE">Maintenance</SelectItem>
                  <SelectItem value="INACTIVE">Inactif</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Liste des véhicules */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-muted-foreground">Chargement des véhicules...</div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-destructive">Erreur: {error}</div>
          </div>
        ) : vehicles.length === 0 ? (
          <Card className="bg-card/50 border-border">
            <CardContent className="p-12 text-center">
              <Car className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Aucun véhicule trouvé</h3>
              <p className="text-muted-foreground">Commencez par ajouter votre premier véhicule.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {vehicles.map((vehicle) => (
              <Card key={vehicle.id} className="bg-card/50 border-border hover:border-primary/50 transition-colors overflow-hidden">
                <CardContent className="p-0">
                  <div className="flex flex-col lg:flex-row">
                    {/* Image du véhicule */}
                    <div className="lg:w-80 lg:h-80 w-full h-48 bg-muted flex items-center justify-center relative overflow-hidden">
                      {vehicle.image ? (
                        <img
                          src={vehicle.image}
                          alt={`${vehicle.make} ${vehicle.model}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center text-muted-foreground">
                          <Car className="w-12 h-12 mb-2" />
                          <span className="text-sm">Aucune image</span>
                        </div>
                      )}
                      
                      {/* Badge de statut sur l'image */}
                      <div className="absolute top-3 left-3">
                        {getStatusBadge(vehicle.status)}
                      </div>
                    </div>

                    {/* Contenu principal */}
                    <div className="flex-1 p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            {getStatusIcon(vehicle.status)}
                            <h3 className="text-lg font-semibold">{vehicle.plate_number}</h3>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
                        <div>
                          <strong>Type:</strong> {vehicle.vehicle_type?.label}
                        </div>
                        <div>
                          <strong>Marque/Modèle:</strong> {vehicle.make} {vehicle.model}
                        </div>
                        {vehicle.year && (
                          <div>
                            <strong>Année:</strong> {vehicle.year}
                          </div>
                        )}
                        <div>
                          <strong>Capacité:</strong> {vehicle.capacity_volume ? `${vehicle.capacity_volume}m³` : 'N/A'} / {vehicle.capacity_weight ? `${vehicle.capacity_weight}kg` : 'N/A'}
                        </div>
                        <div className="flex items-center gap-2">
                          <strong>Assurance:</strong>
                          <span className={`${
                            isExpired(vehicle.insurance_valid_until) ? 'text-red-500' :
                            isExpiringSoon(vehicle.insurance_valid_until) ? 'text-orange-500' :
                            'text-green-500'
                          }`}>
                            {formatDate(vehicle.insurance_valid_until)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <strong>Contrôle technique:</strong>
                          <span className={`${
                            isExpired(vehicle.technical_control_until) ? 'text-red-500' :
                            isExpiringSoon(vehicle.technical_control_until) ? 'text-orange-500' :
                            'text-green-500'
                          }`}>
                            {formatDate(vehicle.technical_control_until)}
                          </span>
                        </div>
                      </div>
                        </div>
                    
                    <div className="flex flex-col items-end gap-3">
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleToggleAvailability(vehicle)}
                          size="sm"
                          variant="outline"
                          className={vehicle.status === 'AVAILABLE' ? 'text-green-600 border-green-600' : 'text-orange-600 border-orange-600'}
                        >
                          {vehicle.status === 'AVAILABLE' ? (
                            <>
                              <ToggleRight className="w-4 h-4 mr-2" />
                              Disponible
                            </>
                          ) : (
                            <>
                              <ToggleLeft className="w-4 h-4 mr-2" />
                              Indisponible
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={() => handleEditVehicle(vehicle)}
                          size="sm"
                          variant="outline"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Modifier
                        </Button>
                        <Button
                          onClick={() => handleDeleteVehicle(vehicle)}
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Supprimer
                        </Button>
                      </div>
                    </div>
                  </div>
                    </div>
                  </div>
                </CardContent>
            </Card>
            ))}
          </div>
        )}

        {/* Dialog pour créer/modifier un véhicule */}
        <Dialog 
          open={vehicleDialogOpen} 
          onOpenChange={(open) => {
            setVehicleDialogOpen(open);
            if (!open) {
              setImagePreview(null);
            }
          }}
        >
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingVehicle ? 'Modifier le véhicule' : 'Ajouter un véhicule'}
              </DialogTitle>
              <DialogDescription>
                {editingVehicle ? 'Modifiez les informations du véhicule.' : 'Ajoutez un nouveau véhicule à votre flotte.'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="plate_number">Plaque d'immatriculation *</Label>
                <Input
                  id="plate_number"
                  value={vehicleData.plate_number}
                  onChange={(e) => setVehicleData(prev => ({ ...prev, plate_number: e.target.value }))}
                  placeholder="Ex: AB-123-CD"
                />
              </div>
              
              <div>
                <Label htmlFor="vehicle_type">Type de véhicule *</Label>
                <Select value={vehicleData.vehicle_type} onValueChange={(value) => setVehicleData(prev => ({ ...prev, vehicle_type: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un type" />
                  </SelectTrigger>
                  <SelectContent>
                    {typesLoading ? (
                      <SelectItem value="" disabled>Chargement...</SelectItem>
                    ) : (
                      vehicleTypes.map((type) => (
                        <SelectItem key={type.code} value={type.code}>
                          {type.label}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="make">Marque</Label>
                  <Input
                    id="make"
                    value={vehicleData.make}
                    onChange={(e) => setVehicleData(prev => ({ ...prev, make: e.target.value }))}
                    placeholder="Ex: Toyota"
                  />
                </div>
                <div>
                  <Label htmlFor="model">Modèle</Label>
                  <Input
                    id="model"
                    value={vehicleData.model}
                    onChange={(e) => setVehicleData(prev => ({ ...prev, model: e.target.value }))}
                    placeholder="Ex: Hilux"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="year">Année</Label>
                <Input
                  id="year"
                  type="number"
                  value={vehicleData.year}
                  onChange={(e) => setVehicleData(prev => ({ ...prev, year: e.target.value }))}
                  placeholder="Ex: 2020"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="capacity_volume">Capacité volume (m³)</Label>
                  <Input
                    id="capacity_volume"
                    type="number"
                    step="0.1"
                    value={vehicleData.capacity_volume}
                    onChange={(e) => setVehicleData(prev => ({ ...prev, capacity_volume: e.target.value }))}
                    placeholder="Ex: 10.5"
                  />
                </div>
                <div>
                  <Label htmlFor="capacity_weight">Capacité poids (kg)</Label>
                  <Input
                    id="capacity_weight"
                    type="number"
                    value={vehicleData.capacity_weight}
                    onChange={(e) => setVehicleData(prev => ({ ...prev, capacity_weight: e.target.value }))}
                    placeholder="Ex: 3500"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="insurance_valid_until">Assurance valide jusqu'au</Label>
                  <Input
                    id="insurance_valid_until"
                    type="date"
                    value={vehicleData.insurance_valid_until}
                    onChange={(e) => setVehicleData(prev => ({ ...prev, insurance_valid_until: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="technical_control_until">Contrôle technique jusqu'au</Label>
                  <Input
                    id="technical_control_until"
                    type="date"
                    value={vehicleData.technical_control_until}
                    onChange={(e) => setVehicleData(prev => ({ ...prev, technical_control_until: e.target.value }))}
                  />
                </div>
              </div>

              {/* Section Image */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="text-lg font-medium">Image du véhicule (facultatif)</h4>
                
                <div className="space-y-4">
                  {/* Aperçu de l'image */}
                  {(imagePreview || editingVehicle?.image) && (
                    <div className="relative w-full h-64 bg-muted rounded-lg overflow-hidden">
                      <img
                        src={imagePreview || editingVehicle?.image}
                        alt="Aperçu du véhicule"
                        className="w-full h-full object-cover"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => {
                          setImagePreview(null);
                          setVehicleData(prev => ({ ...prev, image: undefined }));
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  
                  {/* Bouton d'upload */}
                  <div className="flex items-center gap-3">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                      id="vehicle-image"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('vehicle-image').click()}
                      className="flex items-center gap-2"
                    >
                      <Camera className="h-4 w-4" />
                      {imagePreview || editingVehicle?.image ? 'Changer l\'image' : 'Ajouter une image'}
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      Formats acceptés: JPG, PNG, GIF (max 2MB) - L'image sera automatiquement redimensionnée
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => {
                  setVehicleDialogOpen(false);
                  setImagePreview(null);
                }}
              >
                Annuler
              </Button>
              <Button onClick={handleSubmitVehicle}>
                {editingVehicle ? 'Modifier' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog de confirmation de suppression */}
        <ConfirmationDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          title="Supprimer le véhicule"
          description={`Êtes-vous sûr de vouloir supprimer le véhicule ${vehicleToDelete?.plate_number} ? Cette action est irréversible.`}
          onConfirm={confirmDeleteVehicle}
        />
      </DashboardLayout>
    </>
  );
};

export default TransporterVehicles;
